import { useCallback, useState } from "react";

import { isNative, isWeb, match } from "@stepapp/core";
import { CategoryType } from "@stepapp/core/apiTypes";
import { Block, Button, Touchable, Typography, palette, spacing } from "@stepapp/ui";
import { usePathname, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import {
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputContentSizeChangeEventData,
  ViewStyle,
} from "react-native";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";

type ReservationNoteProps = {
  note: Maybe<string>;
  containerStyles?: StyleProp<ViewStyle>;
  type?: "default" | "edit" | "preview";
  onPress?: () => void;
  onChangeText?: (text: string) => void;
  onChangeTextBlur?: () => void;
  reservationId?: string;
  categoryType?: CategoryType | null;
};

const minInputHeight = spacing.unit * 12;

const ReservationNote = ({
  note = "",
  containerStyles,
  type = "default",
  onPress,
  onChangeText,
  onChangeTextBlur,
  reservationId,
  categoryType = CategoryType.Cleaning,
}: ReservationNoteProps) => {
  const { push } = useRouter();
  const { t } = useTranslation();
  const pathname = usePathname();
  const isEditScreen = pathname.includes("edit-reservation");
  const [customInputHeight, setCustomInputHeight] = useState(minInputHeight);

  const shouldTruncateComment = (note?.length || 0) > 100;
  const truncatedNote = note && shouldTruncateComment ? `${note.slice(0, 100)}...` : note;
  const previewNote = note ? truncatedNote : t("screens.notes.addNoteToOrder");
  const isEditType = type === "edit";

  const openReservationDetails = useCallback(() => {
    push(`/calendar/${reservationId}`);
  }, [push, reservationId]);

  const handleContent = useCallback(
    (content: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      if (isNative) return;

      const contentSize = content?.nativeEvent?.contentSize?.height;

      if (contentSize > minInputHeight) {
        setCustomInputHeight(contentSize);
      } else {
        setCustomInputHeight(minInputHeight);
      }
    },
    [],
  );

  const Wrapper = onPress ? Touchable : Block;

  return (
    <Block style={[isEditScreen && styles.additionalMargin, containerStyles]} id="notes">
      {match({ type, isEditScreen })
        .with({ type: "edit", isEditScreen: false }, () => (
          <>
            <ServiceSceneHeader
              name={t("screens.notes.cleaningNotes", {
                context: categoryType?.toLowerCase(),
              })}
            />
            <ServiceSceneDescription
              description={t("screens.notes.whatWeShouldKnow", {
                context: categoryType?.toLowerCase(),
              })}
            />
          </>
        ))
        .with({ type: "edit", isEditScreen: true }, () => (
          <>
            <Typography variant="largeTitle">
              {t("screens.notes.cleaningNotes", {
                context: categoryType?.toLowerCase(),
              })}
            </Typography>
            <Typography variant="paragraph" type="secondary">
              {t("screens.notes.whatWeShouldKnow", {
                context: categoryType?.toLowerCase(),
              })}
            </Typography>
          </>
        ))
        .otherwise(() => null)}

      <Block style={[styles.noteContainer, isEditType && styles.additionalMargin]}>
        {match({ type })
          .with({ type: "default" }, () => (
            <Wrapper onPress={onPress}>
              <Typography variant="bodyBold" style={styles.noteHeading}>
                {t("screens.notes.notes")}
              </Typography>
              <Typography variant="paragraph" style={styles.note}>
                {note || t("screens.notes.addNoteToOrder")}
              </Typography>
            </Wrapper>
          ))
          .with({ type: "edit" }, () => (
            <Block>
              <Typography variant="bodyBold" style={styles.noteHeading}>
                {t("screens.notes.notes")}
              </Typography>
              <TextInput
                value={note || ""}
                placeholder={t("screens.notes.exampleNote", {
                  context: categoryType?.toLowerCase(),
                })}
                onBlur={onChangeTextBlur}
                onChangeText={onChangeText}
                onContentSizeChange={(content) => handleContent(content)}
                multiline={true}
                placeholderTextColor={palette.font.secondary}
                style={[styles.input, isWeb && { minHeight: customInputHeight }]}
              />
            </Block>
          ))
          .with({ type: "preview" }, () => (
            <Touchable onPress={onPress}>
              <Typography variant="paragraph" style={styles.note}>
                {previewNote}
                {shouldTruncateComment && reservationId && (
                  <Button
                    type="link"
                    label={t("screens.dashboard.seeMore")}
                    onPress={openReservationDetails}
                    textStyle={styles.linkButtonText}
                    style={styles.linkButton}
                  />
                )}
              </Typography>
            </Touchable>
          ))
          .otherwise(() => null)}
      </Block>
    </Block>
  );
};

export default ReservationNote;

const styles = StyleSheet.create({
  additionalMargin: {
    marginTop: spacing.unit * 3,
  },
  linkButton: {
    paddingLeft: 4,
    zIndex: 10,
  },
  linkButtonText: {
    color: palette.common.black,
    fontSize: 15,
    ...Platform.select({
      android: { top: spacing.unit * 2 },
      ios: { top: spacing.unit * 2 },
    }),
  },
  noteContainer: {
    backgroundColor: palette.secondary.butter,
    borderRadius: spacing.radius,
  },
  noteHeading: {
    paddingTop: spacing.unit * 2,
    paddingHorizontal: spacing.unit * 2,
  },
  noteInput: {
    backgroundColor: "transparent",
    padding: spacing.unit * 0,
    borderWidth: 0,
    minHeight: spacing.unit * 8,
    top: spacing.unit,
    width: "100%",
    alignSelf: "flex-start",
  },
  note: {
    paddingHorizontal: spacing.unit * 2,
    paddingVertical: spacing.unit * 2,
    textAlignVertical: "top",
  },
  input: {
    flex: 1,
    width: "100%",
    alignContent: "flex-start",
    fontSize: 16,
    lineHeight: 20,
    textAlignVertical: "top",
    alignSelf: "flex-start",
    color: palette.font.primary,
    textAlign: "auto",
    paddingHorizontal: spacing.unit * 2,
    paddingVertical: spacing.unit * 2,
    ...Platform.select({
      default: { minHeight: spacing.unit * 8 },
      web: { minHeight: minInputHeight },
    }),
  },
});
